import { Component, inject, signal, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { formControl, Validators } from '@al00x/forms';
import {
  ButtonClickEvent,
  CacBaseDialogComponent,
  CacDialogLayoutComponent,
  CacFormBuilderComponent,
  CacTableComponent,
  DataGetRequest,
  formBuilder,
  table,
  TableClass,
} from '@controladad/ng-base';
import { RailEntity } from '@models';
import { RailsApiService, AdminProductsApiService, LazyDropdownComponent, LazyDropdownItem } from '@core';
import { ProductDropdownItem } from '@models';
import { map, take } from 'rxjs';

export interface RailDialogData {
  rail?: RailEntity;
}

export type RailDialogResult = boolean;

@Component({
  selector: 'app-rail-dialog',
  imports: [
    CommonModule,
    CacDialogLayoutComponent,
    CacFormBuilderComponent,
    CacTableComponent,
    LazyDropdownComponent,
  ],
  templateUrl: './rail-dialog.component.html',
  styleUrl: './rail-dialog.component.scss',
})
export class RailDialogComponent extends CacBaseDialogComponent<RailDialogData, RailDialogResult> {
  private readonly railsApi = inject(RailsApiService);
  private readonly productsApi = inject(AdminProductsApiService);
  public override readonly cdr = inject(ChangeDetectorRef);

  isEditMode = false;
  isNotEditable = false;
  selectedProductIds = signal<number[]>([]);

  form = formBuilder({
    cols: 2,
    inputs: {
      nameEnglish: {
        control: formControl<string>(undefined, Validators.required),
        label: 'Name (English)',
        placeholder: 'Enter rail name in English',
      },
      nameArabic: {
        control: formControl<string>(undefined, Validators.required),
        label: 'Name (Arabic)',
        placeholder: 'Enter rail name in Arabic',
      },
      priority: {
        control: formControl<number>(0, Validators.required),
        label: 'Position',
        placeholder: 'Enter position',
        inputType: 'number',
      },
      isActive: {
        control: formControl<boolean>(true, Validators.required),
        label: 'Status',
        type: 'select',
        items: [
          { value: true, label: 'Active' },
          { value: false, label: 'Inactive' },
        ],
      },
    },
  });

  // Product dropdown configuration
  productApiAdapter = this.productsApi.getProductNamesAdapter();

  productItemMapper = (product: ProductDropdownItem): LazyDropdownItem => ({
    id: product.id,
    label: product.name.english || product.name.arabic || 'Unnamed Product',
    value: product.id,
    subtitle: product.name.arabic && product.name.english !== product.name.arabic ? product.name.arabic : undefined,
    sourceData: product,
  });

  getProductsTableItems = (request: DataGetRequest | undefined) => {
    return this.productsApi.getProductNamesAdapter()({
      ...request,
      filters: [
        ...(request?.filters || []),
        { key: 'ProductIds', value: this.selectedProductIds() }
      ],
      pagination: {
        page: 0,
        size: 9999,
      },
    }).pipe(
      map(response => {
        const order = this.selectedProductIds();
        const items = Array.isArray(response) ? response : response.data;

        const sortedItems = items.sort((a: ProductDropdownItem, b: ProductDropdownItem) => {
          const indexA = order.indexOf(a.id);
          const indexB = order.indexOf(b.id);

          if (indexA === -1) return 1;
          if (indexB === -1) return -1;

          return indexB - indexA;
        });

        return Array.isArray(response) ? sortedItems : { ...response, data: sortedItems };
      })
    );
  };

  productsTable = table({
    itemsFn: this.getProductsTableItems,
    pagination: false,
    view: { itemName: 'Product' },
    columns: {
      id: { label: 'ID' },
      'productFirstImage.url': { label: 'Image', type: 'image' },
      'name.english': { label: 'Name (EN)' },
      'name.arabic': { label: 'Name (AR)' },
      'status': { label: 'Status', transform: (visible) => visible ? 'Visible' : 'InVisible', type: 'tag' }
    },
    actions: [
      {
        type: 'icon',
        content: 'trash',
        action: (item: ProductDropdownItem) => this.removeProduct(item.id),
      },
    ],
  });

  constructor() {
    super();
    const rail = this.data?.rail;

    if (rail) {
      this.isEditMode = true;
      this.isNotEditable = rail.notEditable || false;

      // Patch form values
      this.form.patchValue({
        nameEnglish: rail.name.english,
        nameArabic: rail.name.arabic,
        priority: rail.priority,
        isActive: rail.isActive,
      });

      // Load existing products if not editable or if has products
      if (rail.productIds && rail.productIds.length > 0 && !this.isNotEditable) {
        this.selectedProductIds.set(rail.productIds);
      }
    }
  }

  onProductChange(selectedItem: LazyDropdownItem | LazyDropdownItem[] | null): void {
    if (!selectedItem) {
      return;
    }

    const item = Array.isArray(selectedItem) ? selectedItem[0] : selectedItem;
    if (!item) {
      return;
    }

    const currentIds = this.selectedProductIds();

    // Check for duplicates
    if (currentIds.includes(item.id)) {
      snackbar$.error('Product already added');
      return;
    }

    // Add new product ID to the list
    this.selectedProductIds.set([...currentIds, item.id]);
    this.productsTable.refresh();
  }

  removeProduct(productId: number): void {
    const currentIds = this.selectedProductIds();
    const updatedIds = currentIds.filter(id => id !== productId);

    this.selectedProductIds.set(updatedIds);
    this.productsTable.refresh();
  }

  getSelectedProductIds(): number[] {
    return this.selectedProductIds();
  }

  onSubmit(e: ButtonClickEvent): void {
    const values = this.form.getValue();

    if (!values) {
      snackbar$.error('Please fill in all required fields');
      return;
    }

    // Validate products for editable rails
    if (!this.isNotEditable && this.selectedProductIds().length === 0) {
      snackbar$.error('Please add at least one product to the rail');
      return;
    }

    const railData = {
      name: {
        english: values.nameEnglish!,
        arabic: values.nameArabic!,
      },
      productIds: this.isNotEditable ? (this.data?.rail?.productIds || []) : this.selectedProductIds(),
      priority: values.priority!,
      isActive: values.isActive!,
    };

    const request = this.isEditMode
      ? this.railsApi.editRail({
        railId: this.data!.rail!.id!,
        fields: railData,
      })
      : this.railsApi.addRail(railData);

    request.pipe(e.pipe()).subscribe({
      next: () => {
        snackbar$.show(this.isEditMode ? 'Rail updated successfully' : 'Rail created successfully');
        this.submit(true);
      },
      error: (error) => {
        console.error('Error saving rail:', error);
        snackbar$.error('Failed to save rail');
      }
    });
  }
}
